import React, { useState, useRef, forwardRef } from "react";

interface CarouselItem {
  logo: string;
  name: string;
}

interface CarouselInputProps {
  items: CarouselItem[];
  name: string;
  form: any;
  onSelectionChange?: (selectedText: string) => void;
}

export const CarouselInput = forwardRef<HTMLDivElement, CarouselInputProps>(
  ({ items, name, form, onSelectionChange }, ref) => {
    const [currentIndex, setCurrentIndex] = useState<number>(0);
    const [startX, setStartX] = useState<number>(0);
    const [isSwiping, setIsSwiping] = useState<boolean>(false);
    const carouselRef = useRef<HTMLDivElement>(null);

    const nextSlide = (): void => {
      const newIndex = currentIndex === items.length - 1 ? 0 : currentIndex + 1;
      handleSlideChange(newIndex);
    };

    const prevSlide = (): void => {
      const newIndex = currentIndex === 0 ? items.length - 1 : currentIndex - 1;
      handleSlideChange(newIndex);
    };

    const goToSlide = (index: number): void => {
      handleSlideChange(index);
    };

    const handleSlideChange = (index: number) => {
      setCurrentIndex(index);
      const selectedItemText = items[index].name;

      // Update the form value
      form.setFieldValue(name, selectedItemText);
      onSelectionChange?.(selectedItemText);
    };

    const handleTouchStart = (e: React.TouchEvent): void => {
      setStartX(e.touches[0].clientX);
      setIsSwiping(true);
    };

    const handleTouchMove = (e: React.TouchEvent): void => {
      if (!isSwiping) return;

      const currentX = e.touches[0].clientX;
      const diff = startX - currentX;

      if (Math.abs(diff) > 10) {
        e.preventDefault();
      }
    };

    const handleTouchEnd = (e: React.TouchEvent): void => {
      if (!isSwiping) return;

      const endX = e.changedTouches[0].clientX;
      const diff = startX - endX;
      const swipeThreshold = 50;

      if (diff > swipeThreshold) {
        nextSlide();
      } else if (diff < -swipeThreshold) {
        prevSlide();
      }

      setIsSwiping(false);
    };

    const handleMouseDown = (e: React.MouseEvent): void => {
      setStartX(e.clientX);
      setIsSwiping(true);
    };

    const handleMouseMove = (e: React.MouseEvent): void => {
      if (!isSwiping) return;

      const currentX = e.clientX;
      const diff = startX - currentX;

      if (Math.abs(diff) > 10) {
        e.preventDefault();
      }
    };

    const handleMouseUp = (e: React.MouseEvent): void => {
      if (!isSwiping) return;

      const endX = e.clientX;
      const diff = startX - endX;
      const swipeThreshold = 50;

      if (diff > swipeThreshold) {
        nextSlide();
      } else if (diff < -swipeThreshold) {
        prevSlide();
      }

      setIsSwiping(false);
    };

    const handleMouseLeave = (): void => {
      if (isSwiping) {
        setIsSwiping(false);
      }
    };

    const getSlidePosition = (index: number): string => {
      const total = items.length;
      const diff = (index - currentIndex + total) % total;

      if (diff === 0) return "center";
      if (diff === 1) return "right";
      if (diff === total - 1) return "left";
      return "hidden";
    };

    return (
      <div className="w-full mx-auto">
        {/* Carousel Container */}
        <div
          ref={carouselRef}
          className="relative h-48 flex items-center justify-center overflow-hidden"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseLeave}
          style={{ cursor: isSwiping ? "grabbing" : "grab" }}
        >
          {/* Previous Button */}
          <button
            onClick={prevSlide}
            className="absolute left-2 z-20 bg-transparent rounded-full w-10 h-10 flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110"
            aria-label="Previous slide"
            type="button"
          >
            <span className="text-2xl font-bold text-white">‹</span>
          </button>

          {/* Carousel Track */}
          <div className="relative w-full h-full flex items-center justify-center">
            {items.map((item, index) => {
              const position = getSlidePosition(index);
              const isCenter = position === "center";
              const isVisible = position !== "hidden";

              return (
                <div
                  key={`${name}-${index}`}
                  className={`absolute transition-all duration-500 ease-in-out cursor-pointer flex items-center justify-center
                    ${isVisible ? "opacity-100" : "opacity-0 pointer-events-none"}
                    ${
                      position === "center"
                        ? "z-30 translate-x-0"
                        : position === "left"
                          ? "z-20 -translate-x-[90px]"
                          : position === "right"
                            ? "z-20 translate-x-[90px]"
                            : ""
                    }`}
                  onClick={() => goToSlide(index)}
                >
                  <div className="flex flex-col items-center">
                    {/* Image Container */}
                    <div
                      className={`relative overflow-hidden transition-all duration-300 ${
                        isCenter ? "w-[90px] h-[135px]" : "w-[60px] h-[90px]"
                      }`}
                    >
                      <img
                        src={item.logo}
                        alt={item.name}
                        className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                      />
                      {/* Overlay for better text readability on image */}
                      {/* <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" /> */}
                    </div>

                    {/* Text Section - Only show for center slide */}
                    {isCenter && (
                      <div className="mt-3 px-4 py-2 bg-transparent backdrop-blur-sm rounded-lg">
                        <p className="text-white text-center text-sm whitespace-nowrap">
                          {item.name}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Next Button */}
          <button
            onClick={nextSlide}
            className="absolute right-2 z-20 bg-transparent rounded-full w-10 h-10 flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110"
            aria-label="Next slide"
            type="button"
          >
            <span className="text-2xl font-bold text-white">›</span>
          </button>
        </div>

        {/* Index Indicator */}
        <div className="flex flex-col items-center gap-4">
          {/* Text Indicator */}
          <div className="bg-white/30 backdrop-blur-sm px-3 py-1 rounded-full">
            <span className="text-sm font-semibold text-white">
              {currentIndex + 1} / {items.length}
            </span>
          </div>
        </div>
      </div>
    );
  }
);

CarouselInput.displayName = "CarouselInput";
